// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { ArticleConnectionPropertyValue } from '@article/constants/article-connection-property-values';
import { SearchQueryBuilder } from '@project/util/search-query-builder';
import { ConnectionCubeSearchHelper } from '../components/connections/connection-creation/search/connection-cube-search.helper';
import { ConnectionAreaSearchQueryVO } from '@project/components/article/model/vo/connection-area-search-query.vo';
import { ConnectionCubeSearchRequirements } from '../components/connections/connection-creation/search/connection-cube-search-requirements';
import { ArticleDataService } from './article-data.service';
import { ArticleWithTechnicalPropertiesDTO, TechnicalRequirementsVO } from '../properties/technical/technical-requirements';
import { ArticleVO } from '../model/vo/article.vo';
import { Observable, Subject } from 'rxjs';
import { ConnectionSearchResultVO } from '@project/components/article/model/vo/connection-search-result.vo';
import { map } from 'rxjs/operators';
import { ConnectionCubeSearchVector } from '../components/connections/connection-creation/search/connection-cube-search-vector';
import { ArticleTypeConstants } from '../constants/article-type-constants';
import { SearchQueryType } from '@project/components/article/model/vo/search-query-type';
import { GroupedCubesSearchRequirements } from '@project/model/vo/grouped-cubes-search-requirements';
import { ArticleTechDataValueVO } from '../model/vo/article-tech-data.vo';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { FavoriteArticlesFilter } from '@project/components/article/presenter/favorite-articles-filter';
import { UserModel } from '@user/model/user.model';
import { ArticleTypeManager } from '@project/components/article/manager/article-type.manager';
import { AccessorySearchResultVO, ArticleInfoConnectionSetVO } from '@project/components/article/model/vo/accessory-search-result.vo';
import { LabelingCategory } from '../labeling/labeling.category';

export class ConnectionSearchResult {
  vo: ConnectionSearchResultVO;
  connectionCubeVectors: ConnectionCubeSearchVector[];
}

export class AccessorySearchResult {
  vo: AccessorySearchResultVO;
  connectionCubeVectors: ConnectionCubeSearchVector[];
}

export class AccessoryViewData {
  groupedCubesSearchRequirements: GroupedCubesSearchRequirements[];
  searchByArticles: boolean;
  includedArticleTypes: string[] | undefined;
  excludedArticleTypes: string[];
  detachArticleUsageIds: string[] | null;
  replaceArticleUsageIds: string[] | undefined;
  searchType: string;
  searchForMultiplePlacementsOfResultsInSingleSearchVectorEnabled: boolean;
  replaceOperation: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AccessoryService {

  private accessoryViewDataChanged: Subject<AccessoryViewData> = new Subject<AccessoryViewData>();

  public get accessoryViewDataChanged$(): Observable<AccessoryViewData> {
    return this.accessoryViewDataChanged.asObservable();
  }

  public changeAccessoryViewData(accessoryViewData: AccessoryViewData): void {
    this.accessoryViewDataChanged.next(accessoryViewData);
  }

  constructor(private connectionCubeSearchHelper: ConnectionCubeSearchHelper,
              private articleDataService: ArticleDataService,
              private localeResourceManager: LocaleResourceManager,
              private userModel: UserModel,
              private articleTypeManager: ArticleTypeManager) { }

  public getLabelingFavoriteArticlesByRequirementsAndLabelingCategories(
    acceptingCubeSearchRequirements: ConnectionCubeSearchRequirements[],
    labelingCategories: LabelingCategory[],
  ): Observable<ConnectionSearchResult> {

    const searchQueryBuilder = new SearchQueryBuilder(this.connectionCubeSearchHelper);
    searchQueryBuilder.searchByArticles = true;
    const connectionAreaSearchQuery: ConnectionAreaSearchQueryVO =
      searchQueryBuilder.prepareAcceptingConnections(acceptingCubeSearchRequirements).buildQuery();
      // This call was present in the past but had no effect. Include when there might be bugs.
      // connectionAreaSearchQuery.includedArticleTypes = [ArticleTypeConstants.TYPE_LABELING];
    return this.articleDataService
      .getLabelingFavoriteArticlesByRequirementsAndLabelingCategories(connectionAreaSearchQuery, labelingCategories)
      .pipe(map(vo =>  this.toConnectionSearchResult(vo, searchQueryBuilder.connectionCubeVectors)));
  }

  public getBridgeFavoriteArticlesByRequirements(connectionCubeSearchRequirements: ConnectionCubeSearchRequirements[],
                                                 article: ArticleVO | undefined,
                                                 connectionType: string,
                                                 crossSection: number | undefined,
                                                 color: string | undefined): Observable<ConnectionSearchResult> {

    const searchQueryBuilder = new SearchQueryBuilder(this.connectionCubeSearchHelper);
    const connectionAreaSearchQuery: ConnectionAreaSearchQueryVO =
      searchQueryBuilder.prepareAcceptingConnections(connectionCubeSearchRequirements).buildQuery();
    connectionAreaSearchQuery.isSearchForMultiplePlacementsOfResultsInSingleSearchVectorEnabled = true;
    connectionAreaSearchQuery.alternatingJumperUsed = false;

    if (article) {
      connectionAreaSearchQuery.includedArticleIds = [article.identity];
    }

    return this.articleDataService
      .getBridgeFavoriteArticlesByRequirements(connectionAreaSearchQuery, this.isSaddleJumperType(connectionType), crossSection, color)
      .pipe(map(vo => this.toConnectionSearchResult(vo, searchQueryBuilder.connectionCubeVectors)));
  }

  public getArticlesByRequirementsAndArticle(connectionCubeSearchRequirements: ConnectionCubeSearchRequirements[],
                                             article: ArticleVO): Observable<ConnectionSearchResult> {

    const searchQueryBuilder = new SearchQueryBuilder(this.connectionCubeSearchHelper);
    const connectionAreaSearchQuery: ConnectionAreaSearchQueryVO =
      searchQueryBuilder.prepareAcceptingConnections(connectionCubeSearchRequirements).buildQuery();
    connectionAreaSearchQuery.isSearchForMultiplePlacementsOfResultsInSingleSearchVectorEnabled = true;
    connectionAreaSearchQuery.includedArticleIds = [article.identity];
    return this.articleDataService
      .getArticlesByRequirements(connectionAreaSearchQuery, 0, 10, [])
      .pipe(map(vo => this.toConnectionSearchResult(vo, searchQueryBuilder.connectionCubeVectors)));
  }

  public getArticlesByRequirements(connectionCubeSearchRequirements: ConnectionCubeSearchRequirements[]): Observable<ConnectionSearchResult>{
    const searchQueryBuilder = new SearchQueryBuilder(this.connectionCubeSearchHelper);
    const connectionAreaSearchQuery: ConnectionAreaSearchQueryVO =
      searchQueryBuilder.prepareAcceptingConnections(connectionCubeSearchRequirements).buildQuery();

    return this.articleDataService
      .getArticlesByRequirements(connectionAreaSearchQuery, 0, 100, ArticleTypeConstants.EXCLUDED_ARTICLE_TYPES)
      .pipe(map(vo => this.toConnectionSearchResult(vo, searchQueryBuilder.connectionCubeVectors)));
  }

  public getArticlesByGroupedRequirements(groupedCubesSearchRequirements: GroupedCubesSearchRequirements[]): Observable<ConnectionSearchResult> {
    const searchQueryBuilder = new SearchQueryBuilder(this.connectionCubeSearchHelper);
    searchQueryBuilder.searchByArticles = false;
    searchQueryBuilder.searchForMultiplePlacementsOfResultsInSingleSearchVectorEnabled = true;
    searchQueryBuilder.searchType = SearchQueryType.ACCESSORY_ARTICLE;
    const connectionAreaSearchQuery: ConnectionAreaSearchQueryVO
      = searchQueryBuilder.prepareGroupedConnections(groupedCubesSearchRequirements, true).buildQuery();
    // This call was present in the past but had no effect. Include when there might be bugs.
    // connectionAreaSearchQuery.includedArticleTypes = [ArticleTypeConstants.TYPE_LABELING];
    return this.articleDataService
      .getArticlesByRequirements(connectionAreaSearchQuery,0, 200, ArticleTypeConstants.EXCLUDED_ARTICLE_TYPES, false)
      .pipe(map(vo => this.toConnectionSearchResult(vo, searchQueryBuilder.connectionCubeVectors)));
  }

  public getCarrierAlternativeArticlesByRequirements(groupedCubesSearchRequirements: GroupedCubesSearchRequirements[]): Observable<ConnectionSearchResult> {
    const searchQueryBuilder = new SearchQueryBuilder(this.connectionCubeSearchHelper);
    searchQueryBuilder.searchByArticles = false;
    searchQueryBuilder.searchForMultiplePlacementsOfResultsInSingleSearchVectorEnabled = false;
    searchQueryBuilder.searchType = SearchQueryType.CARRIER_CONNECTABLE_ALTERNATIVE_ARTICLE;
    const connectionAreaSearchQuery: ConnectionAreaSearchQueryVO =
      searchQueryBuilder.prepareGroupedConnections(groupedCubesSearchRequirements, true).buildQuery();
    return this.articleDataService
      .getArticlesByRequirements(connectionAreaSearchQuery, 0, 99999, [ArticleTypeConstants.TYPE_FOREIGN_PLUG], false)
      .pipe(map(vo => this.toConnectionSearchResult(vo, searchQueryBuilder.connectionCubeVectors)));
  }

  public getArticleTechData(techDataNames: string[], availableArticleTypeIds: number[]): Observable<ArticleTechDataValueVO[]> {
    return this.articleDataService
      .getArticleTechData(techDataNames, availableArticleTypeIds, this.localeResourceManager.currentLanguage.langKey);
  }

  public getArticlesByTechData(
    searchText: string,
    articleTypeIds: number[],
    series: string[],
    technicalRequirements: TechnicalRequirementsVO[],
    technicalProperties: string[]
  ): Observable<ArticleWithTechnicalPropertiesDTO[]> {

    return this.articleDataService.searchArticlesByRequirements(
      this.localeResourceManager.currentLanguage.langKey,
      searchText,
      articleTypeIds,
      series,
      technicalRequirements,
      technicalProperties);
  }

  public createSearchQueryBuilderForGroup(groupedCubesSearchRequirements: GroupedCubesSearchRequirements[], includedTypes: string[]) {
    const searchQueryBuilder = new SearchQueryBuilder(this.connectionCubeSearchHelper);
    searchQueryBuilder.searchByArticles = true;
    searchQueryBuilder.searchType = SearchQueryType.ACCESSORY_ARTICLE;
    searchQueryBuilder.searchForMultiplePlacementsOfResultsInSingleSearchVectorEnabled = true;

    searchQueryBuilder.includedArticleTypes = includedTypes;
    searchQueryBuilder.prepareGroupedConnections(groupedCubesSearchRequirements);
    const favoriteArticlesFilter = new FavoriteArticlesFilter(this.userModel, this.articleTypeManager);
    searchQueryBuilder.electedArticleIds = favoriteArticlesFilter.getFavoriteArticleIds();
    return searchQueryBuilder;
  }

  public searchAccessoriesForBuilder(searchQueryBuilder: SearchQueryBuilder, excludedArticleTypes: string[]) {
    const connectionAreaSearchQuery: ConnectionAreaSearchQueryVO = searchQueryBuilder.buildQuery();
    return this.articleDataService.searchAccessories(connectionAreaSearchQuery, excludedArticleTypes);
  }

  public searchAccessoriesByGroupWithoutPrintLabeling(groupedCubesSearchRequirements: GroupedCubesSearchRequirements[], includedTypes: string[]) {
    const searchQueryBuilder = this.createSearchQueryBuilderForGroup(groupedCubesSearchRequirements, includedTypes);
    return this.searchAccessoriesForBuilderWithoutPrintLabeling(searchQueryBuilder);
  }

  public searchAccessoriesForBuilderWithoutPrintLabeling(searchQueryBuilder: SearchQueryBuilder,
                                                         excludedArticleTypes: string[] = ArticleTypeConstants.EXCLUDED_ARTICLE_TYPES) {
    const connectionAreaSearchQuery: ConnectionAreaSearchQueryVO = searchQueryBuilder.buildQuery();
    return this.articleDataService
      .searchAccessories(connectionAreaSearchQuery, excludedArticleTypes)
      .pipe(
        map(vo => {
          vo.articleInfoConnectionSet = this.filterOutPrintLabelingAccessories(vo.articleInfoConnectionSet)
          return vo;
        }),
        map(vo => this.toAccessorySearchResult(vo, searchQueryBuilder.connectionCubeVectors)));
  }

  private toConnectionSearchResult(vo: ConnectionSearchResultVO,
                                   connectionCubeVectors: ConnectionCubeSearchVector[]): ConnectionSearchResult {
    return {vo, connectionCubeVectors};
  }

  private toAccessorySearchResult(vo: AccessorySearchResultVO,
                                   connectionCubeVectors: ConnectionCubeSearchVector[]): AccessorySearchResult {
    return {vo, connectionCubeVectors};
  }

  private isSaddleJumperType(connectionType: string): boolean {
    return ArticleConnectionPropertyValue.BRIDGE_SHAFT_CONNECTION_TYPE === connectionType;
  }

  private filterOutPrintLabelingAccessories(accessories: ArticleInfoConnectionSetVO[]): ArticleInfoConnectionSetVO[] {
    const newFilteredAccessories: ArticleInfoConnectionSetVO[] = [];
    for (const accessory of accessories) {
      if (!accessory.isExcludedFromPrintLabeling) {
        newFilteredAccessories.push(accessory);
      }
    }
    return newFilteredAccessories;
  }
}
